import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiSearch, FiFilter, FiStar, FiBriefcase, FiClock, FiChevronRight, FiUser, FiCheckCircle } from 'react-icons/fi';

const API_URL = `${process.env.REACT_APP_API_URL}/api/lawyers`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function LawyerListing({ user }) {
  const [lawyers, setLawyers] = useState([]);
  const [filteredLawyers, setFilteredLawyers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLawyers();
  }, []);

  const fetchLawyers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      if (response.data.success) {
        const lawyersList = response.data.data.map(lawyer => ({
          id: lawyer.id,
          full_name: lawyer.full_name,
          specialization: lawyer.specialty,
          experience_years: lawyer.experience,
          hourly_rate: lawyer.rate,
          rating: lawyer.successRate / 20,
          total_cases: lawyer.cases,
          bio: `Experienced ${lawyer.specialty.toLowerCase()} attorney with ${lawyer.experience} years of practice`,
          verified: lawyer.verified,
          profile_image: lawyer.profile_image
        }));
        setLawyers(lawyersList);
        setFilteredLawyers(lawyersList);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching lawyers:', err);
      setError('Failed to load lawyers');
      setLawyers([]);
      setFilteredLawyers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterLawyers(term, specializationFilter);
  };

  const handleSpecializationFilter = (e) => {
    const spec = e.target.value;
    setSpecializationFilter(spec);
    filterLawyers(searchTerm, spec);
  };

  const filterLawyers = (term, specialization) => {
    let filtered = lawyers;
    if (term) {
      filtered = filtered.filter(lawyer =>
        lawyer.full_name.toLowerCase().includes(term) ||
        lawyer.bio.toLowerCase().includes(term)
      );
    }
    if (specialization) {
      filtered = filtered.filter(lawyer =>
        lawyer.specialization === specialization
      );
    }
    setFilteredLawyers(filtered);
  };

  const specializations = [...new Set(lawyers.map(l => l.specialization))];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-8">
            <div className="h-10 bg-secondary-200 rounded-xl w-80 mx-auto" />
            <div className="h-14 bg-secondary-200 rounded-xl max-w-2xl mx-auto" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 h-72 space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-secondary-200" />
                    <div className="space-y-2 flex-1"><div className="h-4 bg-secondary-200 rounded w-3/4" /><div className="h-3 bg-secondary-200 rounded w-1/2" /></div>
                  </div>
                  <div className="space-y-2"><div className="h-3 bg-secondary-200 rounded" /><div className="h-3 bg-secondary-200 rounded w-4/5" /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
          <div className="absolute bottom-10 left-10 w-60 h-60 rounded-full" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.3) 0%, transparent 70%)' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">
            Find Your Perfect Lawyer
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/60 text-lg max-w-xl mx-auto mb-10">
            Browse from our network of experienced legal professionals
          </motion.p>

          {/* Search & Filter Bar */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
                <input
                  type="text"
                  placeholder="Search by name or expertise..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-12 pr-4 py-3.5 bg-white/10 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-accent-400/50 focus:border-transparent text-sm"
                />
              </div>
              <div className="relative sm:w-56">
                <FiFilter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60 pointer-events-none" />
                <select
                  value={specializationFilter}
                  onChange={handleSpecializationFilter}
                  className="w-full pl-11 pr-4 py-3.5 bg-white/10 border border-white/10 rounded-xl text-white appearance-none focus:outline-none focus:ring-2 focus:ring-accent-400/50 text-sm cursor-pointer"
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="" className="text-secondary-900">All Specializations</option>
                  {specializations.map(spec => (
                    <option key={spec} value={spec} className="text-secondary-900">{spec}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-white/60 text-sm mt-3">{filteredLawyers.length} lawyer{filteredLawyers.length !== 1 ? 's' : ''} found</p>
          </motion.div>
        </div>
      </div>

      {/* Lawyers Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {filteredLawyers.length > 0 ? (
          <motion.div variants={stagger} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLawyers.map(lawyer => (
              <motion.div key={lawyer.id} variants={fadeUp} className="group bg-white rounded-2xl border border-secondary-100 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                        {lawyer.profile_image ? (
                          <img src={`${process.env.REACT_APP_API_URL}${lawyer.profile_image}`} alt={lawyer.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <FiUser className="w-7 h-7 text-primary-400" />
                        )}
                      </div>
                      {lawyer.verified && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center ring-2 ring-white">
                          <FiCheckCircle className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-secondary-900 truncate">{lawyer.full_name}</h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-600 mt-1">
                        {lawyer.specialization}
                      </span>
                    </div>
                  </div>

                  <p className="text-secondary-500 text-sm leading-relaxed mb-5 line-clamp-2">{lawyer.bio}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="flex items-center gap-2 text-sm">
                      <FiClock className="w-4 h-4 text-secondary-400" />
                      <span className="text-secondary-600">{lawyer.experience_years} yrs exp</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiStar className="w-4 h-4 text-accent-500" />
                      <span className="text-secondary-600">{lawyer.rating}/5</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <FiBriefcase className="w-4 h-4 text-secondary-400" />
                      <span className="text-secondary-600">{lawyer.total_cases} cases</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-secondary-400 text-xs font-bold">₹</span>
                      <span className="text-secondary-600">{lawyer.hourly_rate}/hr</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Link to={`/lawyer/${lawyer.id}`} className="flex-1 btn-primary py-2.5 text-sm gap-1.5">
                      View Profile <FiChevronRight className="w-4 h-4" />
                    </Link>
                    {user?.user_type !== 'lawyer' && (
                      <Link to={`/booking?lawyer_id=${lawyer.id}`} className="flex-1 btn-ghost py-2.5 text-sm border border-secondary-200">
                        Book
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-5">
              <FiSearch className="w-8 h-8 text-secondary-400" />
            </div>
            <h3 className="text-xl font-semibold text-secondary-700 mb-2">No lawyers found</h3>
            <p className="text-secondary-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default LawyerListing;
